import { SpinnerFullPage } from '@updater/ui-uds';
import { TrackEventInput } from '@updater/ui-tracker';
import { LUMIS_CHAT_EXPERIMENT } from 'constants/experiments';
import { PageRoutes } from 'constants/pageRoutes';
import { useStore } from 'hooks/useStore';
import { useRouter } from 'next/router';
import { useCallback, useEffect, useRef } from 'react';
import { useTracking as useReactTracking } from 'react-tracking';
import { Events, useTracking } from 'services';
import { appName } from 'services/tracker/constants';
import { useFeature } from 'services/growthbook/useFeature';
import { ChoiceContent } from './ChoiceContent';

export function Choice() {
  const router = useRouter();
  const tracking = useTracking();
  // LUM-380: /provider/chat now keeps the shared page header. The browse path
  // still ensures chrome is visible in case the user is bouncing back from a
  // previously chrome-less page.
  const setHideChrome = useStore((store) => store.setHideChrome);
  const qual = useStore((store) => store.qual);

  // LUM-336 + LUM-434: if the user is bucketed into a wizard variant of
  // `lumis_chat_experiment` (`organic_chat` OR `hybrid_chat`), skip the
  // chat-vs-browse choice gate entirely and route straight into the
  // /offer-wizard funnel. Both variants share the same /offer-wizard route
  // and gating (see OrganicChatPage's `isWizardVariantOn`) — only the
  // non-results render forks. Previously only `organic_chat` redirected, so
  // `hybrid_chat` users (the staging default) landed on the gate and had to
  // type /offer-wizard by hand. We only redirect once GrowthBook has resolved
  // and we have a `qual.promoId` — without the promoId, OrganicChatPage would
  // bounce the user back here, causing a redirect loop.
  const { value: chatVariant, loading: flagLoading } = useFeature(
    LUMIS_CHAT_EXPERIMENT.NAME
  );
  const isWizardVariant =
    chatVariant === LUMIS_CHAT_EXPERIMENT.VARIANTS.ORGANIC_CHAT ||
    chatVariant === LUMIS_CHAT_EXPERIMENT.VARIANTS.HYBRID_CHAT;
  // "First resolution wins" — once we've decided to redirect (or decided
  // not to) for a given mount we don't re-evaluate. Prevents both the
  // double-fire pattern under StrictMode and a hypothetical mid-session
  // GrowthBook re-bucket from queueing a second router.replace.
  const hasRedirected = useRef(false);

  useEffect(() => {
    if (hasRedirected.current || flagLoading) return;
    if (!isWizardVariant || !qual.promoId) return;
    hasRedirected.current = true;
    router.replace(PageRoutes.offerWizard);
  }, [flagLoading, isWizardVariant, qual.promoId, router]);

  // Custom page-view emitter, replacing the shared `usePageEvent` helper
  // so we can SUPPRESS the impression when we're about to redirect to the
  // wizard. Without this, every `organic_chat`-bucketed visit to /choice
  // would record a CHOICE_GATE page-view in analytics even though the
  // gate never rendered — inflating the funnel denominator and skewing
  // gate-to-chat / gate-to-browse conversion rates. We mirror the exact
  // payload shape (`{ domain, object, verb: 'viewed' }`) the shared
  // helper uses so the downstream tracking pipeline sees identical events.
  //
  // Destructure `trackEvent` up front so the effect's deps array tracks
  // the stable function reference (memoised inside react-tracking)
  // rather than the wrapper object — both are stable in production, but
  // the test mock returns a fresh wrapper each render, so depending on
  // the function makes test-vs-prod equivalence less load-bearing on
  // the `hasFiredPageView` ref.
  const { trackEvent: trackReactEvent } =
    useReactTracking<TrackEventInput<unknown>>();
  const hasFiredPageView = useRef(false);
  useEffect(() => {
    if (hasFiredPageView.current) return;
    if (flagLoading) return;
    if (isWizardVariant && qual.promoId) return;
    hasFiredPageView.current = true;
    trackReactEvent({
      domain: appName,
      object: Events.CHOICE_GATE,
      verb: 'viewed',
    });
  }, [flagLoading, isWizardVariant, qual.promoId, trackReactEvent]);

  const handleChatSelect = useCallback(() => {
    tracking.trackEvent(Events.CHOICE_GATE_CHAT_SELECTED, 'clicked');
    router.push(PageRoutes.chat);
  }, [router, tracking]);

  const handleBrowseSelect = useCallback(() => {
    tracking.trackEvent(Events.CHOICE_GATE_BROWSE_SELECTED, 'clicked');
    setHideChrome(false);
    router.push(PageRoutes.quiz);
  }, [router, setHideChrome, tracking]);

  // Hold the render while GrowthBook resolves — without this, the choice
  // gate flashes briefly before the redirect lands the user on the wizard.
  // Once resolved: if the user is in a wizard variant + has a promoId, the
  // effect above has already queued router.replace; render the spinner so
  // we don't show the gate at all in that frame (matches qualify.tsx's
  // pattern for routing-while-resolving).
  if (flagLoading) return <SpinnerFullPage />;
  if (isWizardVariant && qual.promoId) return <SpinnerFullPage />;

  return (
    <ChoiceContent
      onChatSelect={handleChatSelect}
      onBrowseSelect={handleBrowseSelect}
    />
  );
}
